import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BadgeCelebrationModal } from '../components/BadgeCelebrationModal';
import { BADGE_CATALOG } from '../utils/badges';

/**
 * Internal preview page — lets ADMINS replay the badge celebration modal
 * to see exactly what a user experiences when they unlock a badge.
 * Accessible at /preview/badge-celebration — admin-only.
 */
export default function BadgeCelebrationPreview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [badgeCode, setBadgeCode] = useState(null);

  // Admin gate — non-admins get redirected back to dashboard
  if (!user?.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  const badges = Object.values(BADGE_CATALOG);

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-muted-foreground text-sm mb-6 hover:text-white"
        >
          ← Back to dashboard
        </button>
        <h1 className="font-heading text-4xl uppercase tracking-wider mb-2">Badge Celebration Preview</h1>
        <p className="text-muted-foreground mb-8">
          Click any badge below to see exactly what a user sees when they unlock it.
          The video auto-plays and loops. Dismiss with X or the Keep Going button.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {badges.map((b) => (
            <button
              key={b.code}
              onClick={() => setBadgeCode(b.code)}
              className="bg-card border border-border p-6 text-left hover:border-primary/50 transition-colors flex items-center gap-4"
              data-testid={`preview-${b.code}`}
            >
              <img
                src={b.image}
                alt={b.name}
                className="w-16 h-16 object-contain drop-shadow-[0_0_12px_rgba(250,204,21,0.6)]"
              />
              <div className="flex-1">
                <div className="font-heading text-lg text-white">{b.name}</div>
                <div className="text-xs text-muted-foreground">{b.description}</div>
                <div className="text-[10px] uppercase tracking-wider mt-1 text-primary">
                  {b.celebration_video ? 'Has video celebration' : 'Toast only'}
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-10 p-4 bg-card border border-border rounded">
          <div className="text-sm text-muted-foreground">
            <strong className="text-white">Note:</strong> this preview page is for testing only.
            Users see this animation automatically when they log the workout that unlocks a badge —
            no clicks required.
          </div>
        </div>
      </div>

      <BadgeCelebrationModal
        isOpen={!!badgeCode}
        badgeCode={badgeCode}
        onClose={() => setBadgeCode(null)}
      />
    </div>
  );
}
